import { type SectionConfig } from '@/utils/get-matched-section'

/**
 * getMatchedSection matches on exact segment count (no catch-all), so each
 * article depth gets its own entry; 8 covers the deepest imported KB path.
 * The tab title is the last segment; the article renders its own
 * breadcrumb from the content tree, so the header breadcrumb is off.
 */
const DEPTHS = 8

const humanize = (segment: string) =>
  decodeURIComponent(segment)
    .replace(/[-_]+/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())

/** `/knowledge/<prefix>[s0]…[sN]` for each depth, titled from the last segment. */
const byDepth = (prefix: string, title: (last: string) => string) =>
  Array.from({ length: DEPTHS }, (_, i) => {
    const params = Array.from({ length: i + 1 }, (_, j) => `s${j}`)
    return [
      `/knowledge/${prefix}${params.map((p) => `[${p}]`).join('/')}`,
      {
        title: (p: unknown) =>
          title(humanize((p as Record<string, string>)[params[i]] ?? '')),
        showBreadcrumbs: false
      }
    ]
  })

export const sectionConfig: SectionConfig = Object.fromEntries([
  // Not "Knowledge": the header already leads with the app name.
  ['/knowledge', { title: 'Home', showBreadcrumbs: false }],
  // Before the article entries: the first pattern that fits wins, and
  // `[s0]` would otherwise match `_edit`.
  ['/knowledge/_manage', { title: 'Manage pages', showBreadcrumbs: false }],
  ['/knowledge/_edit', { title: 'Edit Home', showBreadcrumbs: false }],
  ...byDepth('_edit/', (last) => `Edit ${last}`),
  ...byDepth('', (last) => last || 'Knowledge')
])
